import {Layout} from "../../component/layout/Layout";
import {useEffect, useMemo, useRef, useState} from "react";
import {WorkflowProgress} from "../../component/layout/WorkflowProgress";
import {Button} from "../../component/form/Button";
import scancard from "../../img/scancard.png"
import example from "../../img/example-frontside-card.jpg"
import exampleNoCard from "../../img/example3.jpg"
import {InputFileFancy} from "../../component/form/InputFileFancy";
import {ReactComponent as IconFile} from "../../img/icon/upload.svg";
import {ReactComponent as IconDelete} from "../../img/icon/delete.svg";
import {InputText} from "../../component/form/InputText";
import {post} from "../../js/api";
import {orderType} from "../../js/constants";
import {empty as emptyAddress} from "../../component/form/AddressInput";
import {showError, showMessage} from "../../js/main";
import {SignOrder} from "../../component/SignOrder";
import {useHistory, useLocation} from "react-router";
import {createSubmissionId, sanitizeFilenamePart, useChangeEffect} from "../../js/util";
import {ImageCaptureHelper} from "./ImageCaptureHelper";
import {InputType} from "../../component/form/InputType";


export const uploadImage = async (side, file) => {
    const date = new Date().getTime();
    const dotIndex = file.name?.lastIndexOf(".") ?? -1;
    const extension = dotIndex >= 0 ? file.name.substring(dotIndex).toLowerCase() : ".jpg";
    const filename = `Fotoupload_${date}_${sanitizeFilenamePart(side)}${extension}`;
    const fileType = file.type || "application/octet-stream";
    const resp = await post("/public/s3/generateStagingUpload", {
        "purpose": "ORDER_IMAGE",
        "filename": filename,
        "fileType": fileType
    });
    const doPut = async () => {
        const uploadResponse = await fetch(resp.httpUploadUri, {
            method: "PUT",
            body: file,
            headers: {
                "Content-Type": fileType,
                "Content-Disposition": "attachment; filename*=UTF-8''" + encodeURIComponent(filename)
            }
        });
        if (!uploadResponse.ok) {
            throw new Error(`Upload failed with status ${uploadResponse.status}`);
        }
    };
    try {
        await doPut();
    } catch (e) {
        await new Promise(r => setTimeout(r, 1000));
        await doPut();
    }

    return {s3identifier: resp.s3identifier, claimToken: resp.claimToken};
}

export const emptyGuestOrderItem = {frontImage: undefined, backImage: undefined, authorization:undefined, keynumber: undefined, amount: undefined, additionalInfo:undefined};

const fileAttachmentFields = {
    foto1: "frontImage",
    foto2: "backImage",
    sicherungskarte: "authorization"
};

let nextGuestOrderItemId = 1;

const createGuestOrderFormItem = () => ({
    id: nextGuestOrderItemId++,
    request: {...emptyGuestOrderItem, amount: 1},
    foto1: undefined,
    foto2: undefined,
    sicherungskarte: undefined,
    foto1DataUrl: null,
    foto2DataUrl: null,
    filesFromHelper: false
});

export const GuestRequestV2 = () => {

    const history = useHistory();
    const location = useLocation();

    const workflowSteps = useMemo(() => ["Start", "Anleitung", "Fotos", "Kontakt", "Bestätigung", "Abschluss"], []);
    const backButtonVisible = useMemo(() => ["Anleitung", "Fotos", "Kontakt"], []);;
    const forwardButtonVisible = useMemo(() => ["Start", "Anleitung", "Fotos", "Kontakt"], []);

    const isMobile = useMemo(() => window.mobileAndTabletCheck(), []);

    const uniqueRef = useRef(new Set());
    const submittingRef = useRef(false);
    const submissionIdRef = useRef();
    if (!submissionIdRef.current) {
        submissionIdRef.current = createSubmissionId();
    }

    const [currentStep, setCurrentStep] = useState(workflowSteps[0]);
    const [submitting, setSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [orderInfoTokenPair, setOrderInfoTokenPair] = useState();

    useEffect(() => {
        const token = new URLSearchParams(location.search).get("request");
        if (token && !orderInfoTokenPair) {
            history.replace(`/guest-request/${token}`);
        }
    }, [history, location.search, orderInfoTokenPair]);

    const [orderItems, setOrderItems] = useState([createGuestOrderFormItem()]);

    const [email, setEmail] = useState();

    const [openHelperItemId, setOpenHelperItemId] = useState(null);

    const startSubmitting = () => {
        if (submittingRef.current) {
            return false;
        }
        submittingRef.current = true;
        setSubmitting(true);
        return true;
    }

    const stopSubmitting = () => {
        submittingRef.current = false;
        setSubmitting(false);
    }

    const setSanitizedEmail = (value) => {
        setEmail(value.replace(/\s/g, ""));
    };

    const updateOrderItem = (itemId, updater) => {
        setOrderItems(currentItems => currentItems.map(item =>
            item.id === itemId ? updater(item) : item
        ));
    }

    const setOrderItemFile = (itemId, field, file) => {
        updateOrderItem(itemId, item => ({
            ...item,
            [field]: file,
            request: {
                ...item.request,
                [fileAttachmentFields[field]]: undefined
            }
        }));
    }

    const addOrderItem = () => {
        setOrderItems(currentItems => [...currentItems, createGuestOrderFormItem()]);
    }

    const removeOrderItem = (itemId) => {
        setOrderItems(currentItems => currentItems.filter(item => item.id !== itemId));
        if (openHelperItemId === itemId) {
            setOpenHelperItemId(null);
        }
    }

    const readDataUrl = (file, setter) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            setter(e.target.result);
        }
        reader.readAsDataURL(file);
    }

    const handleHelperFiles = (itemId, f1, f2) => {
        updateOrderItem(itemId, item => ({
            ...item,
            foto1: f1,
            foto2: f2,
            foto1DataUrl: null,
            foto2DataUrl: null,
            request: {
                ...item.request,
                frontImage: undefined,
                backImage: undefined
            },
            filesFromHelper: true
        }));
        readDataUrl(f1, dataUrl => updateOrderItem(itemId, item => ({...item, foto1DataUrl: dataUrl})));
        readDataUrl(f2, dataUrl => updateOrderItem(itemId, item => ({...item, foto2DataUrl: dataUrl})));
        setOpenHelperItemId(null);
    }

    const countUploadFiles = (items) => items.reduce((count, item) => (
        count +
        (item.foto1 && !item.request.frontImage ? 1 : 0) +
        (item.foto2 && !item.request.backImage ? 1 : 0) +
        (item.sicherungskarte && !item.request.authorization ? 1 : 0)
    ), 0);

    const uploadOrderItem = async (item, index, fileUploaded) => {
        const updated = {...item.request};
        if (!updated.frontImage) {
            updated.frontImage = await uploadImage(`Schluessel_${index + 1}_Seite1`, item.foto1);
            fileUploaded();
        }
        if (!updated.backImage) {
            updated.backImage = await uploadImage(`Schluessel_${index + 1}_Seite2`, item.foto2);
            fileUploaded();
        }
        if(item.sicherungskarte){
            if (!updated.authorization) {
                updated.authorization = await uploadImage(`Schluessel_${index + 1}_Sicherungskarte`, item.sicherungskarte);
                fileUploaded();
            }
        } else {
            updated.authorization = undefined;
        }
        return {...item, request: updated};
    }

    const previousStep = () => {
        const step = workflowSteps[workflowSteps.indexOf(currentStep) - 1 ];
        history.push(`${location.pathname}#${step}`);
        setCurrentStep(step);
        window.scrollTo({top: 0, behavior: "instant"});
    }

    const nextStep = () => {
        const step = workflowSteps[workflowSteps.indexOf(currentStep) + 1 ];
        history.push(`${location.pathname}#${step}`);
        setCurrentStep(step);
        window.scrollTo({top: 0, behavior: "instant"});
    }

    useChangeEffect(() => {
        if (orderInfoTokenPair) return;
        if(history.action == 'POP'){
            const requestedStep = history.location.hash.substring(1);
            // we only allow going back
            if(workflowSteps.indexOf(requestedStep) == -1){
                setCurrentStep(workflowSteps[0]);
            } else if(workflowSteps.indexOf(requestedStep) < workflowSteps.indexOf(currentStep)){
                setCurrentStep(requestedStep);
            }
        }
    }, [history.location]);

    const formSubmitted = async () => {

        switch (currentStep){
            case "Fotos":{
                if (orderItems.some(item => !item.foto1 || !item.foto2)) {
                    showError("Bitte laden Sie für jeden Schlüssel zwei Fotos hoch.");
                    return;
                }

                if (!startSubmitting()) {
                    return;
                }
                const totalUploadFiles = countUploadFiles(orderItems);
                if (totalUploadFiles > 0) {
                    setUploadProgress({uploaded: 0, total: totalUploadFiles});
                }
                try {
                    const fileUploaded = () => setUploadProgress(currentProgress => currentProgress
                        ? {...currentProgress, uploaded: currentProgress.uploaded + 1}
                        : currentProgress
                    );
                    const uploadedItems = [];
                    for (let i = 0; i < orderItems.length; i++) {
                        uploadedItems.push(await uploadOrderItem(orderItems[i], i, fileUploaded));
                    }
                    setOrderItems(uploadedItems);
                    nextStep();
                } catch (e) {
                    showError("Fotos konnten nicht hochgeladen werden. Bitte versuchen Sie es erneut.");
                } finally {
                    setUploadProgress(null);
                    stopSubmitting();
                }
                return;
            }
            case "Kontakt":{
                if (!startSubmitting()) {
                    return;
                }
                try {
                    await new Promise(r => setTimeout(r, 1000));
                    const items = [];
                    for (let i = 0; i < orderItems.length; i++) {
                        items.push(await post("/guestRequests/createNewOrderItem", orderItems[i].request));
                    }
                    const orderReq = {
                        summary: "Fotobestellung Schlüssel für " + email,
                        shippingInfo: undefined,
                        billingAddress: {...emptyAddress, email: email},
                        paymentType: undefined,
                        type: orderType.request,
                        submissionId: submissionIdRef.current,
                        items
                    };
                    const o = await post("/public/orders", orderReq, true);
                    showMessage("Anfrage gespeichert. Sie haben eine Mail mit dem Bestätigungscode erhalten.");
                    setOrderInfoTokenPair(o);
                    setCurrentStep("Bestätigung");
                    // A reload can resume the existing request without uploading or creating it again.
                    const search = new URLSearchParams(location.search);
                    search.set("request", o.token);
                    history.replace({...location, search: `?${search}`, hash: "#Bestätigung"});
                    window.scrollTo({top: 0, behavior: "instant"});
                } catch (e) {
                    showError("Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.");
                } finally {
                    stopSubmitting();
                }
                return;
            }
        }

        nextStep();
    }

    return <>
        <Layout username={null}>
            <form className="guest-request-v2" onSubmit={(e) => {
                e.preventDefault();
                formSubmitted();
            }}>
                {uploadProgress && <div className="guest-request-upload-overlay" role="status" aria-live="polite" aria-busy="true">
                    <div className="guest-request-upload-dialog">
                        <div className="guest-request-upload-spinner" aria-hidden="true"></div>
                        <h2>Fotos werden hochgeladen</h2>
                        <p>{uploadProgress.uploaded} von {uploadProgress.total} Fotos hochgeladen</p>
                    </div>
                </div>}

                <WorkflowProgress steps={workflowSteps} currentStep={currentStep}></WorkflowProgress>
                {currentStep == 'Start' && <>
                    <h1>Machen Sie zwei Fotos von Ihrem Schlüssel</h1>
                    <p>Um Ihre Anfrage schnell und genau beantworten zu können, benötigen wir zwei gute Fotos (von jeder Seite eines). <br/><br/>
                        Legen Sie den Schlüssel auf einen Untergrund mit möglichst viel Kontrast, oder laden Sie Sich die Scancard runter, drucken diese aus und legen Sie den Schlüssel auf die Scancard. Ganz wie Sie wollen.</p>

                    <div className="scancard-img-container">
                        <a href="https://key-direct-production-bucket.s3.eu-central-1.amazonaws.com/public/Scancard.pdf" target="_blank">
                            <img className="scancard-img" src={scancard} />
                        </a>
                        <Button width={300} onClick={e => {
                            e.preventDefault()
                            window.open("https://key-direct-production-bucket.s3.eu-central-1.amazonaws.com/public/Scancard.pdf");
                        }
                        }>ScanCard herunterladen (PDF)</Button>
                    </div>

                    <h2>Preisliste</h2>
                    <p>Sie bekommen nach Abschluss ein individuelles Angebot mit dem Preis für Ihren Schlüssel. Bis dahin finden Sie <a href={"/price-list"}>hier eine Preisübersicht.</a></p>
                </>}
                {currentStep == 'Anleitung' && <>
                    <h1>So machen Sie die Fotos Ihres Schlüssels</h1>
                    <p>Legen Sie Ihren Schlüssel entweder auf einen Untergrund mit ausreichend Kontrast, oder auf die Scancard.</p>
                    <div className="default">
                        <div className={"example-img-container"}>
                            <h2>Ausrichtung mit Scancard</h2>
                            <img src={example} className="example-img"/>
                        </div>
                        <div>
                            <div className={"example-img-container"}>
                                <h2>Ausrichtung ohne Scancard</h2>
                                <img src={exampleNoCard} className="example-img"/>
                            </div>
                        </div>
                    </div>
                    <div className={"default"}>
                        <div>
                            <h2>Wichtige Hinweise</h2>
                            <ul>
                                <li>Sorgen Sie für eine gute Beleuchtung</li>

                                <li>Schlüssel vollständig sichtbar</li>
                                <li>Beide Seiten fotografieren</li>
                                <li>Bei Scancard: alle umgebenden Muster sichtbar</li>
                            </ul>
                        </div>
                    </div>
                </>}
                {currentStep == 'Fotos' && <>
                    <h1>Bilder der Schlüssel hochladen</h1>
                    <p>Tipp: Verwenden Sie die Scancard für beste Ergebnisse</p>

                    {orderItems.map((item, index) => <div className="guest-request-key-entry" key={item.id}>
                        <div className="guest-request-key-entry-header">
                            <h2>Schlüssel {index + 1}</h2>
                            {orderItems.length > 1 && <button type="button"
                                                              className="guest-request-key-remove no-link-hover"
                                                              aria-label={`Schlüssel ${index + 1} entfernen`}
                                                              title="Schlüssel entfernen"
                                                              onClick={() => removeOrderItem(item.id)}>
                                <IconDelete/>
                            </button>}
                        </div>

                        {isMobile && !item.filesFromHelper && <>
                            <div className="image-input-container">
                                <div>
                                    <button type="button"
                                            className="image-input"
                                            onClick={() => setOpenHelperItemId(item.id)}>
                                        <label>
                                            <IconFile className="file-svg file-svg-center"/>
                                            <span>Mit Aufnahme-Hilfe</span>
                                        </label>
                                    </button>
                                </div>
                            </div>
                        </>}
                        {openHelperItemId === item.id && <div className="guest-request-image-helper">
                            <ImageCaptureHelper setter={(f1, f2) => handleHelperFiles(item.id, f1, f2)}
                                                close={() => setOpenHelperItemId(null)}></ImageCaptureHelper>
                        </div>}
                        {item.filesFromHelper && <div className="default" style={{margin: '10px 0px'}}>
                            <div className="image-input-container">
                                <div>
                                    <button type="button" className="image-input" style={{marginBottom: '10px'}}>
                                        <label>
                                            {item.foto1DataUrl && <img src={item.foto1DataUrl} className="preview-img" alt="Foto 1"/>}
                                            <span>Foto 1</span>
                                        </label>
                                    </button>
                                </div>
                            </div>
                            <div className="image-input-container">
                                <div>
                                    <button type="button" className="image-input">
                                        <label>
                                            {item.foto2DataUrl && <img src={item.foto2DataUrl} className="preview-img" alt="Foto 2"/>}
                                            <span>Foto 2</span>
                                        </label>
                                    </button>
                                </div>
                            </div>
                        </div>}
                        {!item.filesFromHelper && isMobile && <h2>oder</h2>}
                        {!item.filesFromHelper && <>
                            <div className="default">
                                <InputFileFancy name="foto1"
                                                concatId={item.id}
                                                required={true}
                                                label={"Bild der ersten Seite"}
                                                setter={file => setOrderItemFile(item.id, "foto1", file)}
                                                initialFile={item.foto1}
                                                maxMBSize="10"
                                                uniqueRef={uniqueRef}></InputFileFancy>
                                <InputFileFancy name="foto2"
                                                concatId={item.id}
                                                required={true}
                                                label={"Bild der zweiten Seite"}
                                                setter={file => setOrderItemFile(item.id, "foto2", file)}
                                                initialFile={item.foto2}
                                                maxMBSize="10"
                                                uniqueRef={uniqueRef}></InputFileFancy>
                            </div>
                        </>}

                        <h2>Sicherungskarte oder Berechtigung (falls vorhanden)</h2>
                        <p>Nur bei bestimmten Schlüsseln notwendig - wir prüfen das automatisch. Wenn Sie unsicher sind, überspringen Sie diesen Punkt.</p>
                        <div className="default">
                            <InputFileFancy name="sicherungskarte"
                                            concatId={item.id}
                                            label="Sicherungskarte"
                                            setter={file => setOrderItemFile(item.id, "sicherungskarte", file)}
                                            initialFile={item.sicherungskarte}
                                            maxMBSize={10}
                                            accept={"image/*,application/pdf"}></InputFileFancy>
                        </div>
                    </div>)}

                    <button type="button" className="secondary-button mt-1 mb-1" onClick={addOrderItem}>weiteren Schlüssel hinzufügen</button>
                    <div className={"zeichnung"}><p>Hinweis: Wir prüfen automatisch, ob eine Berechtigung erforderlich
                        ist. Falls ja, melden wir uns.</p></div>
                </>}
                {currentStep == 'Kontakt' && <>
                    <h1>Ihre Kontaktdaten</h1>
                    <h2>Kontaktdaten</h2>
                    <div className="default">
                        <InputType type={"email"} required={true} label={"E-Mail"} value={email} setter={setSanitizedEmail}></InputType>
                    </div>
                    <div className="zeichnung">
                        <h2>Das ist keine Bestellung - nur eine Anfrage!</h2>
                        <p>Nach Prüfung erhalten Sie ein konkretes Angebot mit Preis, Versandkosten und Lieferzeit. <br/>
                        <strong>Unverbindlich & Kostenlos</strong><br/>
                        <strong>Einfach absenden - wir melden uns!</strong></p>
                    </div>
                </>}
                {currentStep === "Abschluss" && <>
                    <h1>Vielen Dank - Ihre Anfrage ist eingegangen!</h1>
                    <p>Ihre Anfrage wird geprüft. In Kürze erhalten Sie eine E-Mail mit folgenden Informationen:</p>
                    <ul>
                        <li>Erfolgreiche Prüfung der Schlüsselbilder</li>
                        <li>Rückmeldung, ob zusätzliche Unterlagen oder eine Berechtigung erforderlich sind</li>
                        <li>Konkretes Angebot mit Preis pro Schlüssel inkl. Versandkosten</li>
                        <li>Voraussichtliche Lieferzeit</li>
                    </ul>
                    <h2>Informationen zur Bezahlung und zum Ablauf</h2>
                    <p>Wir starten die Produktion erst nach Bestätigung des Angebots, welches Sie erhalten werden.</p>
                    <Button type="button" theme="primary-button" width={230}
                            onClick={() => history.push(`/guest-request/${orderInfoTokenPair.token}`)}>
                        Zur Anfrage
                    </Button>
                </>}
                <div className={"safe-actions mt-1"}>
                    {backButtonVisible.indexOf(currentStep) >= 0 && <Button type={"button"} key="back" theme="secondary-button mr-1 mb-1" width={230}
                            submitting={submitting} onClick={e => {
                        e.preventDefault();
                        previousStep();
                    }}>zurück</Button>}
                    {forwardButtonVisible.indexOf(currentStep) >= 0 && <Button key="submit" theme="primary-button mb-1" width={230}
                            submitting={submitting}>Weiter</Button>}
                </div>

            </form>
            {currentStep === "Bestätigung" && orderInfoTokenPair && <div className="guest-request-v2">
                <SignOrder order={orderInfoTokenPair.info} orderToken={orderInfoTokenPair.token}
                           isPublic={true} cancelable={false}
                           signed={updated => {
                               setOrderInfoTokenPair(current => ({...current, info: updated}));
                               setCurrentStep("Abschluss");
                               history.replace({...location, hash: "#Abschluss"});
                               window.scrollTo({top: 0, behavior: "instant"});
                           }}
                           tanFailed={() => history.replace(`/guest-request/${orderInfoTokenPair.token}`)}/>
            </div>}
        </Layout>
    </>
}
